import { Edit } from '@mui/icons-material';
import { Grid2, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Article } from '@thro-types/billing';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetArticlesListQuery } from 'api/hooks/billing/articles';
import { DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { DATEFORMATWITHTIME } from 'app/containers/Billing/utils';
import { translations } from 'locales/i18n';
import { formatMonetaryValue } from 'utils/formatMonetaryValue';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { ArticleDrawer } from './ArticleDrawer';

export const ArticleTab = () => {
  const { t } = useTranslation();

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'name', sort: 'asc' }],
    defaultPageSize: 100,
    sortKey: 'article_sort',
    paginationKeys: { page: 'article_page', pageSize: 'article_pageSize' }
  });

  const [open, setOpen] = useState(false);

  const [article, setArticle] = useState<Article | undefined>(undefined);

  const { data, isFetching } = useGetArticlesListQuery({
    params: {
      skip: page * pageSize,
      take: pageSize,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort,
      search: debouncedSearchText
    }
  });

  const rowActions = useCallback(
    (params: GridRowParams<Article>) => {
      return [
        <GridActionsCellItem
          icon={<Edit />}
          label={t(translations.common.edit())}
          onClick={() => {
            setArticle(params.row);
            setOpen(true);
          }}
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<Article> => {
    return [
      { field: 'name', flex: 2, headerName: t(translations.common.name()) },
      {
        field: 'description',
        flex: 2,
        headerName: t(translations.common.description()),
        renderCell: ({ value }) => value
      },
      {
        field: 'defaultTax',
        flex: 1,
        headerName: t(translations.common.tax()),
        renderCell: params => `${params.row.defaultTax?.key} - ${params.row.defaultTax?.text}`
      },
      {
        field: 'unit',
        flex: 0.5,
        headerName: t(translations.common.unit())
      },
      {
        field: 'price',
        flex: 0.8,
        headerAlign: 'right',
        align: 'right',
        headerName: t(translations.common.price()),
        renderCell: params => formatMonetaryValue(params.row.price)
      },
      {
        field: 'updatedAt',
        flex: 1,
        headerName: t(translations.common.updatedAt()),
        renderCell: params => (
          <Typography variant="body2">
            {format(new Date(params.row.updatedAt), DATEFORMATWITHTIME, { locale: de })}
          </Typography>
        )
      },
      {
        field: 'actions',
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);

  return (
    <Grid2 style={{ flexGrow: 1 }}>
      <ArticleDrawer open={open} setOpen={setOpen} article={article} />
      <Grid2 container justifyContent={'flex-start'} paddingBottom={1}>
        <Searchfield handleSearch={handleSearchTextChange} search={search} />
      </Grid2>
      <Grid2 container style={{ height: '80vh' }}>
        <DataGrid
          columns={columns}
          rows={data?.data}
          disableColumnResize
          getRowHeight={() => 'auto'}
          //? THIS IS RECOMMENDED BY MUI IF AUTO IS USED
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' }
          }}
          loading={isFetching}
          disableColumnMenu
          disableRowSelectionOnClick
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          sortingMode="server"
          sortModel={sortingModel}
          onSortModelChange={handleSortModelChange}
          rowCount={data?.count || 0}
        />
      </Grid2>
    </Grid2>
  );
};
